import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvGrid from "@directv-digital/dds.ui.dtv-grid";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import ChannelLogoChip from "@/shared/ChannelLogoChip";
import React, { useState, useEffect, useRef } from "react";
import Tooltip from "@directv-digital/fusion.molecules.tooltip";
import { LocalRsnApiResponse } from "@directv-digital/dds.apis.local-rsn-chnls";
import { colorPalette, MBL_SIZE, TBL_SIZE } from "@directv-digital/dds.ui.dtv-theme";
import ZipcodeModule, {
  ZipcodeModuleProps
} from "src/components/video-lab/zipcode-module";
import Collapse from "@mui/material/Collapse";
import { useBreakPoints } from "@directv-digital/dds.utils";
import { DownIcon, UpIcon } from "@directv-digital/fusion.atoms.icons";
import Divider from "@directv-digital/fusion.molecules.divider";
import Button from "@directv-digital/fusion.atoms.button";
import { handleLocalRsnChannelFilter, handleNationalChannelDMAFilter, filterSignatureExcludedLocalChannels } from "lib/service/channels/get-local-national-dma-eligible-channels";
import ChannelInfoIcon from "@/shared/ChannelInfoIcon";

type EmbeddedSvodIcon = {
  icon: {
    mobile?: string;
    tablet?: string;
    default: string;
    desktop?: string;
    alt?: string;
  };
  disable?: boolean;
  name?: string;
};

export type InPageChannelPackLineupProps = {
  id: string;
  channelTypes: any;
  description: string;
  availableChannels: any;
  /**
   * WAT-11327: queryParam of the primary package (`channelTypes[0]`), resolved from
   * package-config by the adapter. Used as the `genreLocalChannelMapping` /
   * `genreNationalMapping` lookup key so duo/multi-word packs resolve the same way
   * ChnlListingPageV2 does. Falls back to the package-ID-derived short-name when absent.
   */
  packageQueryParam?: string;
  genreLocalChannelMapping?: any;
  signatureExcludedLocalChannels?: any[];
  genreNationals?: any;
  zipcodeModuleConfig?: ZipcodeModuleProps["ciConfig"];
  mostPopularChannels?: string[];
  embeddedSvodIcons?: EmbeddedSvodIcon[];
  channelsTitle?: string;
  subscriptionsTitle?: string;
};

const styles = {
  headingContainer: {
    px: {
      xs: "20px",
      sm: "32px",
      md: "48px"
    },
    pb: {
      xs: "2px",
      sm: "2px",
      md: "2px"
    }
  },
  descriptionContainer: {
    px: {
      xs: "0px",
      sm: "0px",
      md: "0px"
    }

  },
  description: {
    /* Desktop/UI/Label Small */
    color: `${colorPalette.gray["300"]}`,
    fontSize: "12px !important",
    lineHeight: "130%" /* 15.6px */
  },
  channelGridContainer: {
    display: "flex",
    justifyContent: {
      xs: "center",
      sm: "start"
    },
    alignItems: "flex-start",
    alignContent: "flex-start",
    gap: {
      xs: "20px 5px",
      sm: "16px",
      md: "16px"
    },
    alignSelf: "stretch",
    flexWrap: "wrap"
  },
  channelLogoContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    gap: "8px",
    position: "relative"
    // width: {
    //   xs: "82px !important",
    //   sm: "88px !important",
    // },
    // p: "12px 9px 12px 9px",
    // background: "#FBFBFB",
  },
  channelLogo: {
    display: "flex",
    width: "80px",
    height: "80px",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "300px",
    // WAT-11362: contain so landscape OV logos aren't cropped (no-op for square logoUrl).
    objectFit: "contain"
    // WAT-11362: the circular badge + centring geometry live on the logo img itself
    // (channelLogoSx), applied at the render site.
  },
  tooltipStyles: {
    maxWidth: "230px",
    opacity: "1",
    transform: "none",
    transition: "opacity 200ms cubicBezier(0.4, 0, 0.2, 1) 0ms, transform 133ms cubicBezier(0.4, 0, 0.2, 1) 0ms",
    transformOrigin: "left center",
    backgroundColor: "rgba(97, 97, 97, 0.92)",
    borderRadius: "4px",
    color: "#fff",
    padding: "4px 8px",
    fontSize: "0.6875rem",
    width: "fit-content",
    margin: "0",
    wordWrap: "break-word",
    fontWeight: "500",
    position: "relative",
    marginLeft: "0px!important"
  }

};

const _isNationalForGenre = (
  localRsnChannel: any,
  packageKey: string,
  dma: string,
  nielsenDma?: string,
  genreNationalMapping?: any
): boolean => {
  const isLocalChannelForNational = genreNationalMapping?.find?.(
    channel => channel?.channel === localRsnChannel?.network
  );
  if (!isLocalChannelForNational) {
    return false;
  }
  const isLocalBelongToNational = isLocalChannelForNational ? ![undefined, null, ""]?.includes?.(isLocalChannelForNational?.[packageKey]) : false;
  const isNationalChannelInDma = isLocalBelongToNational && (
    isLocalChannelForNational?.[packageKey]?.split?.(",").includes?.(dma?.toString?.() || nielsenDma?.toString?.())
  );
  return isNationalChannelInDma;
};

export function sortByKey<T>(array: T[], key: keyof T, mostPopularChannels: string[]): T[] {
  // Removing most popular channels coming from cms and adding to the start of the returned array
  const popChannels = [];
  if (mostPopularChannels && mostPopularChannels.length > 0) {
    for (const channel of mostPopularChannels) {
      const index = array.findIndex(
        item => (item[key] as string).trim().toLowerCase() ===
          channel.trim().toLowerCase()
      );
      if (index !== -1) {
        popChannels.push(array.splice(index, 1)[0]);
      }
    }
  }

  const restChannels = array.sort((a, b) => {
    const valueA = a[key] as string;
    const valueB = b[key] as string;
    return valueA.localeCompare(valueB);
  });
  return [...popChannels, ...restChannels];
}

export function InPageChannelPackLineup(props: InPageChannelPackLineupProps) {
  try {
    const [availableAllChannels, setAvailableAllChannels] = useState(
      sortByKey([...props.availableChannels], "channelNameLabel", props?.mostPopularChannels)
    );

    const { isMobile } = useBreakPoints();
    const collapseHeight = isMobile ?
      4 * 80 + 3 * 20 + 25 :
      6 * 80 + 5 * 16 + 30; // 4 rows for mobile, 6 rows for desktop

    const [showSeeMoreButton, setShowSeeMoreButton] = useState(false);

    const [loading, setLoading] = useState(true);
    const zipCodeModalRef = useRef<HTMLDivElement>(null);
    const [_accordionSummaryTop, setAccordionSummaryTop] = useState<number>(0);
    const [iconPlacements, setIconPlacements] = useState<Record<number, "top" | "bottom">>({});

    const signaturePackages = [
      "ATT_TV_ENTERTAINMENT",
      "ATT_TV_CHOICE",
      "ATT_TV_ULTIMATE",
      "ATT_TV_PREMIER",
      "DTV_OPTIMO_MAS"
    ];

    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
      setShowSeeMoreButton(
        document.getElementById("channelbody")?.scrollHeight > collapseHeight
      );
    }, [availableAllChannels, isMobile]);

    useEffect(() => {
      const modalElement = zipCodeModalRef.current;
      if (!modalElement) {
        return;
      }

      const observer = new ResizeObserver(() => {
        const height = modalElement.offsetHeight || 0;
        setAccordionSummaryTop(height - 4);
      });

      observer.observe(modalElement);

      return () => {
        observer.disconnect();
      };
    }, []);

    const handleTooltipPlacement = (channel, chnlindex, index) => {
      if (channel?.isLocal || channel?.isRsn || channel?.isNational) {
        const iconEl = document?.getElementById?.(`icon-container-${index}-${chnlindex}`);
        const headerEl = document?.getElementById?.(`mainHeader`);
        if (iconEl && headerEl) {
          const tooltipBox = iconEl?.getBoundingClientRect();
          const headerBox = headerEl?.getBoundingClientRect();
          const isOverlappingHeader = tooltipBox?.top > headerBox?.bottom;
          setIconPlacements(prev => ({
            ...prev,
            [`${index}-${chnlindex}`]: isOverlappingHeader ? "bottom" : "top"
          }));
        }
      }
    };

    /** Local Rsn API Logic -- Start -- */

    // WAT-11327: key genreLocalChannelMapping / genreNationalMapping by the primary package's
    // queryParam (adapter-injected, resolved from package-config), falling back to the
    // package-ID-derived short-name. Mirrors ChnlListingPageV2's shortNameToQueryParam
    // normalization — the ID-derived form misses for duo/multi-word packs (e.g.
    // ATT_TV_MYENTERTAINMENT_MYNEWS -> "myentertainment_mynews" != feed key "duomyentertainmentmynews").
    // NOTE: single-package (channelTypes[0]) only. A combined config where the same local network
    // is local under one package but national under another cannot be represented per-package here
    // and is deferred — see WAT-11327 follow-up for the multi-package override design.
    const pkgname = (
      props?.packageQueryParam?.trim() || props?.channelTypes?.[0]?.replace(/^ATT_TV_/, "") || ""
    ).toLowerCase();

    // Filter local channels based on DMA eligible & genreEligible & queryParameter
    const handleAddLocalChannelsToPackages = (localRsnChannels, dma, nielsenDma) => {
      let filteredLocalChannels = [];
      if (signaturePackages.includes(props?.channelTypes?.[0])) {
        // Generated by Copilot- Start - 2026-04-23 09:00:00
        // Filter out signature-excluded locals (e.g., Telemundo in genre-only DMAs)
        // Pass packageId for per-package exclusion support
        filteredLocalChannels = filterSignatureExcludedLocalChannels(localRsnChannels, dma, props?.signatureExcludedLocalChannels, props?.channelTypes?.[0]);
        // Generated by Copilot- End - 2026-04-23 09:00:00
      } else {
        filteredLocalChannels = handleLocalRsnChannelFilter(localRsnChannels, props?.genreLocalChannelMapping, pkgname, dma, nielsenDma, props?.genreNationals?.genreNationalMapping, props?.channelTypes?.[0]);
      }

      filteredLocalChannels = filteredLocalChannels.map(channel => ({
        ...channel,
        channelNameLabel: channel?.network,
        channelLogoPath: channel?.logo,
        isLocal: channel?.TYPE === "LOCAL",
        isRsn: channel?.TYPE === "RSN"
      }));
      // Genre Local/National DMA Eligibility check - start
      let updatedNationalChannels = [];
      let updatedLocalChannels = [];
      try {
        const result = handleNationalChannelDMAFilter({
          filteredLocalChannels,
          nationalChannels: [...(availableAllChannels || [])],
          currentDma: dma || nielsenDma,
          nationalMapping: props?.genreNationals?.genreNationalMapping || [],
          packageKey: pkgname
        });
        updatedNationalChannels = result?.filteredNationalChannels || [];
        updatedLocalChannels = result?.updatedLocalChannels || [];
      } catch (err) {
        console.error("Error in [InPageChannelPackLineup] handleNationalChannelDMAFilter:", err);
        updatedNationalChannels = [];
        updatedLocalChannels = filteredLocalChannels || [];
      }
      // Genre Local/National DMA Eligibility check - end
      setAvailableAllChannels(
        sortByKey([...updatedLocalChannels, ...updatedNationalChannels], 'channelNameLabel', props?.mostPopularChannels)
      );
      setLoading(false);
    };

    const setLoadingLocalRsnChannels = React.useCallback(() => {
      setLoading(true);
    }, []);

    const addLocalRsnChnls = React.useCallback((channels: LocalRsnApiResponse | null, dma?: string, nielsenDma?: string) => {
      const { localChannels = [], rsnChannels = [] } = channels;
      console.log("Channel Pack Lineup fetched LocalRsn Chnls >>", localChannels, rsnChannels);
      handleAddLocalChannelsToPackages([...(localChannels || []), ...(rsnChannels || [])], dma, nielsenDma);
    }, []);

    const handleLocalRsnApiError = error => {
      setLoading(false);
      console.error("Channel Pack Lineup >> Local/Rsn API Error..", error);
    };
    /** Local Rsn API Logic -- End -- */

    const handleCollapseButtonClick = event => {
      if (expanded) {
        setExpanded(!expanded);
        const element = document.getElementById(
          "inpagechannel-pck-lnp-zip-module"
        );
        if(element) {
          element.scrollIntoView();
        }
      } else {
        setExpanded(!expanded);
      }
      event.currentTarget.blur();
    };

    const getAllChannels = availableAllChannels => availableAllChannels?.map((channel, chnlindex) => {
      const genreNationalData =
          props?.genreNationals?.genreNationalMapping?.find(
            item => item.channel === channel?.channelNameLabel
          );
      return (
          <DtvBox
            className={`channel-tooltip-${chnlindex}`}
            id={`icon-container-${chnlindex}-${chnlindex}`}
            key={chnlindex}
            tabIndex={-1}
            sx={{
              ...styles.channelLogoContainer
            }}
            onMouseEnter={_event => (channel?.isLocal || channel?.isRsn || channel?.isNational ?
                handleTooltipPlacement(channel, chnlindex, chnlindex) :
                undefined)
            }
            onTouchStart={_event => (channel?.isLocal || channel?.isRsn || channel?.isNational ?
                handleTooltipPlacement(channel, chnlindex, chnlindex) :
                undefined)
            }
          >
            {(channel?.channelNameLabel &&
            <Tooltip
              describeChild
              closeOnScroll={true}
              content={channel?.channelNameLabel}
              disableCursorPointer={true}
              enterTouchDelay={0}
              key={chnlindex}
              tabIndex={-1}
              tooltipStyles={styles.tooltipStyles}
              PopperProps={{
                disablePortal: false,
                modifiers: [
                  {
                    name: "preventOverflow",
                    enabled: true
                  },
                  {
                    name: "flip",
                    enabled: true
                  },
                  {
                    name: "offset",
                    options: {
                      offset: [0, -20]
                    }
                  }
                ],
                sx: {
                  width: "unset !important",
                  zIndex: 9999,
                  "& .MuiTypography-root": {
                    fontFamily: `roboto-regular`,
                    fontSize: "14px"
                  }
                }
              }}
              placement={
                channel?.isLocal || channel?.isRsn || channel?.isNational ?
                  iconPlacements[`${0}-${chnlindex}`] ?? "top" :
                  "bottom"
              }
              sx={{
                "& #popper[data-popper-escaped]": {
                  visibility: "hidden"
                }
              }}
            >
              {loading ? (
                <DtvBox sx={{
 ...styles.channelLogo,
paddingTop: "1.3px"
}}>
                  <svg viewBox="0 0 261 261" xmlns="http://www.w3.org/2000/svg">
                    <g><circle cx="108" cy="108" data-name="Ellipse 34" fill="#E4E4E4" r="108" transform="translate(22.5 20.5)" /></g>
                  </svg>
                </DtvBox>
              ) : (
                <ChannelLogoChip
                  key={chnlindex}
                  alt={channel?.channelNameLabel || "channel logo"}
                  imgSx={styles.channelLogo}
                  size="80px"
                  isOvLogo={channel?.isOvLogo}
                  src={channel?.channelLogoPath}
                />
              )}
            </Tooltip>
            )}
            {(channel?.isLocal || channel?.isRsn || (channel?.isNational && genreNationalData?.nationaltooltipContent)) && (
              <Tooltip
                closeOnScroll={true}
                enterTouchDelay={0}
                placement="top"
                PopperProps={{
                  disablePortal: true,
                  sx: {
                    "& .MuiTooltip-tooltip": {
                      backgroundColor: `${colorPalette.neutral_white.main} !important`,
                      color: "#102641 !important",
                      fontWeight: "400 !important",
                      fontSize: "14px !important",
                      position: "relative",
                      boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.15)",
                      padding: "12px",
                      zIndex: 9999
                    },
                    "& .MuiTypography-root": {
                      fontFamily: `roboto-regular`,
                      fontSize: "14px"
                    }
                  }
                }}
                content={
                  channel?.isNational ?
                    genreNationalData?.nationaltooltipContent || "" :
                    channel?.isLocal ?
                    "Local channels aren't available everywhere and may vary by location. You may need to be in your billing region (ZIP Code) to watch them." :
                    "Regional Sports Networks aren't available everywhere and may vary by package and location. You may need to be in your billing region (ZIP Code) to watch them."
                }
              >
                <DtvBox
                  component="div"
                  sx={{
                    cursor: "pointer",
                    width: "100%",
                    margin: "0 auto"
                  }}
                >
                  <DtvBox sx={{
                    position: "absolute",
                    bottom: "-6px",
                    left: "16px",
                    maxWidth: "80px",
                    maxHeight: "80px",
                    display: "flex",
                    alignItems: "center",
                    gap: "2px",
                    backgroundColor: colorPalette.gray[300],
                    borderRadius: "4px",
                    m: "-4px auto 0px auto",
                    p: "5px"
                  }}>
                    <DtvText
                      content={channel?.isNational ? genreNationalData?.nationalTooltipLabel : channel?.isLocal ? "Local" : "RSN"}
                      variant="smalltext"
                      sx={{
                        color: "#FFFFFF",
                        textAlign: "center",
                        fontSize: "10px !important"
                      }}
                    />
                    <ChannelInfoIcon color={colorPalette.white.main} />
                  </DtvBox>
                </DtvBox>
              </Tooltip>
            )}
          </DtvBox>
      );
    });

    const getChannelgridComponent = () => (
        <DtvBox
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            width: "100%",
            position: "relative"
          }}
        >
          {/* heading */}
          <DtvBox
            id="mainHeader"
            ref={zipCodeModalRef}
          >
            {/* Zip Code Module */}
            <DtvGrid>
              <ZipcodeModule
                ciConfig={props?.zipcodeModuleConfig}
                id="inpagechannel-pck-lnp-zip-module"
                mode="light"
                position="left"
                size={isMobile ? "small" : "medium"}
                localRsnApiOptions={{
 satellite: false,
stream: true
}}
                onLocalRsnChnlsApiError={handleLocalRsnApiError}
                onLocalRsnChnlsConfirmed={addLocalRsnChnls}
                onSearchButtonClick={setLoadingLocalRsnChannels}
              />
            </DtvGrid>
            <DtvGrid
              sx={{
                ...styles.descriptionContainer,
                paddingBottom: {
 xs: "24px",
md: "32px"
}
              }}
            >
              <DtvText
                content={props.description}
                sx={{ ...styles.description }}
              />
            </DtvGrid>
          </DtvBox>
          {/* Only show subscriptions and channels label if there are embeddedSvodIcons */}
          {(props?.embeddedSvodIcons?.length > 0) &&
              renderSubscriptions()
          }
          {props?.channelsTitle &&
          <DtvBox sx={{ width: "100%" }}>
                <DtvText
                  content={props?.channelsTitle}
                  variant="body2medium"
                  sx={{
 fontWeight: 500,
paddingBottom: "8px"
}}
                />
              </DtvBox>
          }
          {/* body */}
          {showSeeMoreButton ? (
            <Collapse
              collapsedSize={collapseHeight}
              in={expanded}
              timeout={"auto"}
            >
              <DtvGrid id="channelbody" sx={{ ...styles.channelGridContainer }}>
                {getAllChannels(availableAllChannels)}
              </DtvGrid>
            </Collapse>
          ) : (
            <DtvGrid id="channelbody" sx={{ ...styles.channelGridContainer }}>
              {getAllChannels(availableAllChannels)}
            </DtvGrid>
          )}
          {showSeeMoreButton && (
            <DtvBox
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "30px"
              }}
            >
              <Divider
                sx={{
                  "& .MuiDivider-wrapper": {
                    padding: "0px"
                  }
                }}
              >
                <Button
                  buttontype="secondary"
                  rightIcon={expanded ? <UpIcon /> : <DownIcon />}
                  size="small"
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    "& svg svg path": {
                      fill: "#102641"
                    },
                    "&:focus": {
                      "& svg svg path": {
                        fill: "#ffffff !important"
                      }
                    },
                    "&:hover": {
                      color: "#ffffff",
                      backgroundColor: "#102641",
                      "& svg svg path": {
                        fill: "#ffffff !important"
                      }
                    }
                  }}
                  onClick={handleCollapseButtonClick}
                >
                  {expanded ? "Show less" : "Show more"}
                </Button>
              </Divider>
            </DtvBox>
          )}
        </DtvBox>
    );

    // Generated by Copilot- Start
    const renderSubscriptions = () => {
      const icons = props.embeddedSvodIcons || [];
      const hasIcon = icons.some(iconObj => iconObj.icon && Object.keys(iconObj.icon).length > 0);
      if (!hasIcon) {
        return null;
      }
      return (
        <DtvBox sx={{
 width: "100%",
paddingBottom: {
 xs: "24px",
md: "29px"
}
}}>
          <DtvBox sx={{ paddingBottom: "8px" }}>
          <DtvText
            content={props?.subscriptionsTitle || "Subscriptions"}
            sx={{ fontWeight: 500 }}
            variant="body2medium"
          />
          </DtvBox>
          <DtvGrid
            sx={{
              display: "grid",
              gap: "14px",
              gridTemplateColumns: {
                xs: "repeat(auto-fill, 100px)"
              },
              justifyItems: "start",
              alignItems: "center"
            }}
          >
            {icons.map((iconObj, idx) => {
              const iconData = iconObj.icon;
              return (
                <Tooltip
                  closeOnScroll={true}
                  content={iconObj?.name || ''}
                  enterTouchDelay={0}
                  key={idx}
                  placement="bottom"
                  tooltipStyles={styles.tooltipStyles}
                  PopperProps={{
                    disablePortal: false,
                    sx: {
                      width: "unset !important",
                      zIndex: 9999,
                      "& .MuiTypography-root": {
                        fontFamily: `roboto-regular`,
                        fontSize: "14px"
                      }
                    }
                  }}
                >
                  <DtvBox sx={{
 display: "flex",
alignItems: "center",
justifyContent: "center"
}}>
                    {iconData && (
                      <picture>
                        <source media={MBL_SIZE} srcSet={iconData?.mobile || iconData?.default} />
                        <source media={TBL_SIZE} srcSet={iconData?.tablet || iconData?.default} />
                        <img
                          alt={iconData?.alt || 'subscription icon'}
                          src={iconData?.desktop || iconData?.default}
                          style={{
 width: "64px",
height: "64px",
display: "block"
}}
                        />
                      </picture>
                    )}
                  </DtvBox>
                </Tooltip>
              );
            })}
          </DtvGrid>
        </DtvBox>
      );
    };
    // Generated by Copilot- End

    return (
      <DtvBox
        id={props.id}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          margin: {
 xs: "24px 20px",
            sm: "32px 24px",
            md: "32px 140px"
}

        }}
      >
        {getChannelgridComponent()}
      </DtvBox>
    );
  } catch (error) {
    return <ErrorDisplay component="DTV ChannelPackLineup component" error={error} />;
  }
}
